import { useQuery } from "@tanstack/react-query";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area, Legend,
} from "recharts";
import { api } from "../lib/api";
import { fmtBRL } from "../lib/format";
import { computeDelta } from "../lib/delta";
import { useFilters, periodToDates } from "../lib/filters";
import { ChartCard } from "../components/ChartCard";
import { PageHeader } from "../components/PageHeader";
import { IconAlertTriangle } from "../components/icons";
import { KpiTile, SparkLine } from "../components/intelligence/KpiTile";
import type { TransportCostDto, InsightsDashboardDto } from "@bi/types";
import { QueryErrorBanner } from "../components/QueryErrorBanner";

const MONTH_PT_SHORT = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];

function monthLabel(ym: string): string {
  const [y, m] = ym.split("-");
  return MONTH_PT_SHORT[parseInt(m ?? "1", 10) - 1] ?? ym;
}

// Cores do pie de composição de custos
const PIE_COLORS: Record<string, string> = {
  "Frete motorista": "var(--brand)",
  "Pedágios": "#F59E0B",
  "Outros pagamentos": "#9CA3AF",
};

export function CostsPage({ embedded = false }: { embedded?: boolean }) {
  const { filters } = useFilters();
  const { from, to } = periodToDates(filters.period, filters.customFrom, filters.customTo);

  const { data: costs, isLoading, isError: costsError, refetch: costsRefetch } = useQuery({
    queryKey: ["transport", "costs", from, to, filters.comparison],
    queryFn: () =>
      api.get<TransportCostDto>(
        `/dashboards/transport/costs?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&comparison=${filters.comparison}`,
      ),
    staleTime: 60_000,
  });

  const { data: insightsDash, isError: insightsDashError, refetch: insightsDashRefetch } = useQuery({
    queryKey: ["insights", "dashboard"],
    queryFn: () => api.get<InsightsDashboardDto>("/insights/dashboard"),
    staleTime: 120_000,
  });

  // ── Deltas ──────────────────────────────────────────────────────────────────
  const totalDelta = computeDelta(costs?.totalPaid, costs?.totalPaidPrev, { direction: "higher_is_worse" });
  const freightDelta = computeDelta(costs?.driverFreight, costs?.driverFreightPrev, { direction: "neutral" });
  const tollDelta = computeDelta(costs?.tollTotal, costs?.tollPrev, { direction: "higher_is_worse" });

  // ── Custo por CT-e ─────────────────────────────────────────────────────────
  const costPerCte = costs && costs.cteCount > 0
    ? costs.totalPaid / costs.cteCount
    : null;

  // ── Pie de composição ──────────────────────────────────────────────────────
  const pieData = costs
    ? [
        { name: "Frete motorista", value: costs.driverFreight },
        { name: "Pedágios",        value: costs.tollTotal },
        {
          name: "Outros pagamentos",
          value: Math.max(0, costs.totalPaid - costs.driverFreight - costs.tollTotal),
        },
      ].filter((d) => d.value > 0)
    : [];

  // ── Tendência de pagamentos ────────────────────────────────────────────────
  const monthlyData = costs?.monthlyPayments ?? [];
  const monthlyPaid = monthlyData.map((m) => m.paid);

  // ── Top fornecedores ───────────────────────────────────────────────────────
  const topSuppliers = costs?.topSuppliers ?? [];
  const maxSupplierPaid = Math.max(...topSuppliers.map((s) => s.totalPaid), 1);
  const totalSuppliersPaid = topSuppliers.reduce((s, x) => s + x.totalPaid, 0);

  // ── Custo por veículo ─────────────────────────────────────────────────────
  const costByVehicle = costs?.costByVehicle ?? [];

  // ── Insights ainda não tratados (status "new"; não existe status "active") ──
  const insights = (insightsDash?.recentInsights ?? [])
    .filter((i) => i.status === "new")
    .slice(0, 3);

  return (
    <div className="space-y-6">
      {!embedded && (
        <PageHeader
          title="Custos"
          subtitle="Pagamentos realizados, frete motorista, pedágios e fornecedores"
        />
      )}

      {(costsError || insightsDashError) && <QueryErrorBanner onRetry={() => { costsRefetch(); insightsDashRefetch(); }} />}

      {/* KPIs — clicáveis para a memória de cálculo (mesmo período da tela) */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiTile
          label="Total pago no período"
          value={costs ? fmtBRL(costs.totalPaid) : "—"}
          delta={totalDelta}
          deltaLabel="vs. mês anterior"
          memory={{ cardKey: "pago-no-periodo", periodFrom: from, periodTo: to }}
          visual={<SparkLine values={monthlyPaid} label="Total pago por mês" />}
          footnote="Pago nos últimos meses. O ponto marca o mês atual"
          loading={isLoading}
        />
        <KpiTile
          label="Frete motorista"
          value={costs ? fmtBRL(costs.driverFreight) : "—"}
          delta={freightDelta}
          deltaLabel="vs. mês anterior"
          memory={{ cardKey: "dre-frete-motorista", periodFrom: from, periodTo: to }}
          loading={isLoading}
        />
        <KpiTile
          label="Pedágios (CT-e)"
          value={costs ? fmtBRL(costs.tollTotal) : "—"}
          delta={tollDelta}
          deltaLabel="vs. mês anterior"
          memory={{ cardKey: "cte-pedagio-mes", periodFrom: from, periodTo: to }}
          loading={isLoading}
        />
        <KpiTile
          label="Custo por CT-e"
          value={costPerCte != null ? fmtBRL(costPerCte) : "—"}
          footnote={costs ? `Valor derivado: total pago ÷ ${costs.cteCount.toLocaleString("pt-BR")} CT-es emitidos` : "Valor derivado: total pago ÷ CT-es emitidos"}
          loading={isLoading}
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard
          title="Composição de Custos"
          subtitle="Frete motorista · Pedágios · Outros pagamentos"
          height={260}
        >
          {pieData.length === 0 ? (
            <p className="text-sm text-gray-400 text-center pt-16">Sem dados no período</p>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey="value"
                  nameKey="name"
                  cx="45%"
                  cy="50%"
                  outerRadius={100}
                  innerRadius={50}
                  label={({ name, percent }) =>
                    percent > 0.06 ? `${(percent * 100).toFixed(0)}%` : ""
                  }
                  labelLine
                >
                  {pieData.map((d, i) => (
                    <Cell key={i} fill={PIE_COLORS[d.name] ?? "#6B7280"} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(v: number) => fmtBRL(v)}
                  contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e7ecef" }}
                />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="Evolução de Pagamentos" subtitle="Últimos 6 meses" height={260}>
          {monthlyData.length === 0 ? (
            <p className="text-sm text-gray-400 text-center pt-16">Sem dados no período</p>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={monthlyData} margin={{ top: 5, right: 5, left: -15, bottom: 0 }}>
                <defs>
                  <linearGradient id="grad-pay" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="var(--brand)" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="var(--brand)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="month"
                  tickFormatter={monthLabel}
                  tick={{ fontSize: 11, fill: "#9ca3af" }}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
                  tick={{ fontSize: 11, fill: "#9ca3af" }}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip
                  formatter={(v: number) => [fmtBRL(v), "Pagamentos"]}
                  labelFormatter={monthLabel}
                  contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e7ecef" }}
                />
                <Area
                  type="monotone"
                  dataKey="paid"
                  stroke="var(--brand)"
                  strokeWidth={2}
                  fill="url(#grad-pay)"
                  dot={{ fill: "var(--brand)", r: 4 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>

      {/* Suppliers + Vehicle costs */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-surface-border">
            <p className="text-sm font-semibold text-gray-900">Top Fornecedores por Pagamento</p>
            <p className="text-xs text-gray-400 mt-0.5">Pagamentos realizados no período</p>
          </div>
          <div className="divide-y divide-surface-border">
            {isLoading ? (
              <p className="px-5 py-4 text-sm text-gray-400">Carregando...</p>
            ) : topSuppliers.length === 0 ? (
              <p className="px-5 py-4 text-sm text-gray-400">Nenhum pagamento no período</p>
            ) : (
              topSuppliers.map((s, i) => (
                <div key={i} className="px-5 py-3 flex items-center gap-3">
                  <span className="text-xs font-bold text-gray-400 w-5">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{s.name}</p>
                    <div className="mt-1.5 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-warning-500 rounded-full transition-all" style={{ width: `${Math.min(Math.max((s.totalPaid / maxSupplierPaid) * 100, 2), 100)}%` }} />
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-semibold text-gray-900">{s.formatted}</p>
                    <p className="text-xs text-gray-400">
                      {totalSuppliersPaid > 0 ? `${((s.totalPaid / totalSuppliersPaid) * 100).toFixed(1)}% do total` : "—"}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <ChartCard title="Frete por Veículo" subtitle="Carta frete: gross freight por centro de custo" height={240}>
          {isLoading ? (
            <p className="text-sm text-gray-400 text-center pt-16">Carregando...</p>
          ) : costByVehicle.length === 0 ? (
            <p className="text-sm text-gray-400 text-center pt-16">Sem cartas frete no período</p>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={costByVehicle}
                layout="vertical"
                margin={{ top: 0, right: 15, left: 10, bottom: 0 }}
              >
                <defs>
                  <linearGradient id="grad-frete-veiculo" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%"  stopColor="var(--brand)" stopOpacity={0.55} />
                    <stop offset="100%" stopColor="var(--brand)" stopOpacity={0.95} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                <XAxis
                  type="number"
                  tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
                  tick={{ fontSize: 11, fill: "#9ca3af" }}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  type="category"
                  dataKey="plate"
                  tick={{ fontSize: 11, fill: "#9ca3af" }}
                  tickLine={false}
                  axisLine={false}
                  width={70}
                />
                <Tooltip
                  formatter={(v: number) => [fmtBRL(v), "Frete bruto"]}
                  contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e7ecef" }}
                />
                <Bar
                  dataKey="freight"
                  fill="url(#grad-frete-veiculo)"
                  radius={[0, 4, 4, 0]}
                  label={{ position: "right", formatter: (v: number) => fmtBRL(v), fontSize: 10, fill: "#6b7280" }}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>

      {/* Insights */}
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3 flex items-center gap-2">
          <IconAlertTriangle className="w-3.5 h-3.5" />
          Insights Ativos
        </h3>
        {insights.length === 0 ? (
          <p className="text-sm text-gray-400">Nenhum insight ativo no momento.</p>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {insights.map((ins) => (
              <div key={ins.id} className="card px-4 py-3 space-y-1">
                <div className="flex items-start gap-2">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                    ins.severity === "critical" ? "bg-danger-50 text-danger-700"
                    : ins.severity === "warning" ? "bg-amber-50 text-amber-700"
                    : "bg-blue-50 text-blue-700"
                  }`}>
                    {ins.severity === "critical" ? "Crítico" : ins.severity === "warning" ? "Atenção" : "Info"}
                  </span>
                </div>
                <p className="text-sm font-semibold text-gray-800">{ins.title}</p>
                <p className="text-xs text-gray-500 line-clamp-3">{ins.description}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
